/*
 * Brickminded — Free-shipping currency helper (shared by the
 * announcement bar and the cart).
 *
 * Most free-shipping thresholds are configured in EUR (the shop
 * currency). Shopify exposes the live shop -> presentment exchange rate
 * to the storefront as window.Shopify.currency.rate, so we convert the
 * fixed EUR figure into whatever currency the customer is shopping in —
 * the same mechanism the previous theme used and which the store is
 * already known to populate. If the rate is missing for any reason we
 * fall back to rate = 1 / EUR, i.e. we show the untouched EUR figure
 * rather than something wrong.
 *
 * Not every threshold is an EUR figure, though. The Swiss zone is free
 * from CHF 200 flat — a number the checkout compares against directly, not
 * a converted one. Carrying it as EUR would have advertised ~186 CHF and
 * broken the promise at the till. So thresholds come through
 * snippets/free-shipping-threshold.liquid tagged with their currency, and
 * conversionFactor() decides whether a rate applies at all:
 *
 *   threshold in EUR, shopper in DKK  -> factor = the shop rate
 *   threshold in CHF, shopper in CHF  -> factor = 1 (already native)
 *   threshold in CHF, shopper in EUR  -> null; no rate exists for that
 *                                        direction, so callers leave the
 *                                        server-rendered figure alone
 *                                        rather than invent one.
 */
(function () {
  // Idempotent: the announcement bar, the cart and the PDP each load this
  // helper, so guard against defining it (and re-running convertAmounts) twice.
  if (window.BMFreeShipping) return;

  // Format amounts in the storefront's own language, so the grouping matches the
  // surrounding text (a German store shows "1.500 Ft", a Hungarian one "1 500 Ft").
  // The currency symbol itself always comes from the shopper's presentment currency.
  function pageLocale() {
    var lang = document.documentElement && document.documentElement.lang;
    if (!lang && window.Shopify && window.Shopify.locale) lang = window.Shopify.locale;
    return lang || 'de';
  }

  function rateInfo() {
    var c = window.Shopify && window.Shopify.currency;
    var rate = c && c.rate ? parseFloat(c.rate) : 1;
    if (!rate || isNaN(rate)) rate = 1;
    return {
      rate: rate,
      active: (c && c.active) || 'EUR'
    };
  }

  // Whole-currency amounts only (no minor units) — a shipping promise of
  // "ab 750 kr" reads better than "ab 745,83 kr".
  function format(amount, currency) {
    try {
      return new Intl.NumberFormat(pageLocale(), {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return Math.round(amount) + ' ' + currency;
    }
  }

  // Round a converted threshold UP so the promise reads cleanly. The
  // krona/złoty/leu/koruna family rounds to whole tens (746 kr -> 750 kr); every
  // other currency rounds up to 3 significant figures (100 € -> 31021 Ft ->
  // 31100 Ft) while already-round figures like 100 € stay 100 €. Always rounds
  // up, so we never advertise a lower threshold than the real one.
  var ROUND_TO_TEN = { DKK: true, SEK: true, PLN: true, RON: true, CZK: true };
  function roundUpNice(value, currency) {
    if (!(value > 0)) return value;
    var step;
    if (ROUND_TO_TEN[currency]) {
      step = 10;
    } else {
      var digits = Math.floor(Math.log10(value)) + 1;
      step = Math.pow(10, Math.max(0, digits - 3));
    }
    return Math.ceil(value / step) * step;
  }

  // Multiplier that turns an amount denominated in `currency` into the
  // shopper's presentment currency, or null when no such multiplier exists.
  // Every caller that compares a threshold against a price must go through
  // this — the raw window.Shopify.currency.rate is only correct for EUR
  // thresholds, and applying it to a natively-billed one would misstate the
  // offer in whichever direction the rate happens to sit.
  function conversionFactor(currency) {
    var info = rateInfo();
    if (!currency) currency = 'EUR';
    if (currency === info.active) return 1;
    if (currency === 'EUR') return info.rate;
    return null;
  }

  // Convert standalone threshold labels:
  //   <span data-bm-fs-amount data-fs-value="100" data-fs-currency="EUR">100 €</span>
  function convertAmounts(root) {
    var info = rateInfo();
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-bm-fs-amount]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var value = parseFloat(el.getAttribute('data-fs-value'));
      if (isNaN(value)) continue;
      var factor = conversionFactor(el.getAttribute('data-fs-currency'));
      // No factor: the server-rendered label is already the honest figure.
      if (factor === null) continue;
      // factor !== null means the result lands in the presentment currency,
      // either by conversion or because it was already denominated there.
      el.textContent = format(roundUpNice(value * factor, info.active), info.active);
    }
  }

  window.BMFreeShipping = {
    rateInfo: rateInfo,
    conversionFactor: conversionFactor,
    format: format,
    convertAmounts: convertAmounts
  };

  if (document.readyState !== 'loading') {
    convertAmounts();
  } else {
    document.addEventListener('DOMContentLoaded', function () { convertAmounts(); });
  }
})();
